import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../hooks/usePermissions';
import GlobalHeader from '../components/GlobalHeader';
import MembersSection from '../components/organization/MembersSection';
import InvitationsSection from '../components/organization/InvitationsSection';
import ApiKeysSection from '../components/organization/ApiKeysSection';
import OrganizationInfoSection from '../components/organization/OrganizationInfoSection';
import BillingInfoSection from '../components/organization/BillingInfoSection';
import InvoicesSection from '../components/organization/InvoicesSection';
import './OrganizationSettingsPage.css';

// Tabs disponibles
const VALID_TABS = ['info', 'members', 'invitations', 'apikeys', 'billing', 'invoices'];

function OrganizationSettingsPage() {
    const location = useLocation();
    const navigate = useNavigate();
    const { user, loading } = useAuth();
    const { canManage } = usePermissions();

    // Lire le tab depuis l'URL hash
    const getTabFromHash = useCallback(() => {
        const hash = location.hash.replace('#', '');
        return VALID_TABS.includes(hash) ? hash : 'info';
    }, [location.hash]);

    const [activeTab, setActiveTab] = useState(getTabFromHash);

    // Mettre à jour le tab quand l'URL change
    useEffect(() => {
        setActiveTab(getTabFromHash());
    }, [getTabFromHash]);

    // Fonction pour changer de tab et mettre à jour l'URL
    const handleTabChange = (tab) => {
        setActiveTab(tab);
        navigate(`#${tab}`, { replace: true });
    };

    if (loading) {
        return (
            <>
                <GlobalHeader />
                <div className="organization-settings">
                    <div className="loading">Chargement...</div>
                </div>
            </>
        );
    }

    if (!user?.organization_id) {
        return (
            <>
                <GlobalHeader />
                <div className="organization-settings">
                    <div className="error">Organisation non trouvée</div>
                </div>
            </>
        );
    }

    return (
        <>
            <GlobalHeader />
            <div className="organization-settings">
                <div className="settings-header">
                    <h1>Paramètres de l'organisation</h1>
                </div>

                <div className="settings-tabs">
                    <button
                        className={`tab ${activeTab === 'info' ? 'active' : ''}`}
                        onClick={() => handleTabChange('info')}
                    >
                        Informations
                    </button>
                    <button
                        className={`tab ${activeTab === 'members' ? 'active' : ''}`}
                        onClick={() => handleTabChange('members')}
                    >
                        Membres
                    </button>
                    {canManage && (
                        <button
                            className={`tab ${activeTab === 'invitations' ? 'active' : ''}`}
                            onClick={() => handleTabChange('invitations')}
                        >
                            Invitations
                        </button>
                    )}
                    {canManage && (
                        <button
                            className={`tab ${activeTab === 'apikeys' ? 'active' : ''}`}
                            onClick={() => handleTabChange('apikeys')}
                        >
                            Cles API
                        </button>
                    )}
                    {canManage && (
                        <button
                            className={`tab ${activeTab === 'billing' ? 'active' : ''}`}
                            onClick={() => handleTabChange('billing')}
                        >
                            Facturation
                        </button>
                    )}
                    {canManage && (
                        <button
                            className={`tab ${activeTab === 'invoices' ? 'active' : ''}`}
                            onClick={() => handleTabChange('invoices')}
                        >
                            Factures
                        </button>
                    )}
                </div>

                <div className="settings-content">
                    {activeTab === 'info' && (
                        <OrganizationInfoSection organizationId={user.organization_id} />
                    )}
                    {activeTab === 'members' && (
                        <MembersSection organizationId={user.organization_id} />
                    )}
                    {canManage && activeTab === 'invitations' && (
                        <InvitationsSection organizationId={user.organization_id} />
                    )}
                    {canManage && activeTab === 'apikeys' && (
                        <ApiKeysSection organizationId={user.organization_id} />
                    )}
                    {canManage && activeTab === 'billing' && (
                        <BillingInfoSection organizationId={user.organization_id} />
                    )}
                    {canManage && activeTab === 'invoices' && (
                        <InvoicesSection organizationId={user.organization_id} />
                    )}
                </div>
            </div>
        </>
    );
}

export default OrganizationSettingsPage;
